import { Box, Card, Grid, Typography } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { object, string, ref, ValidationError } from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import styles from "../../common/user/user.module.scss";
import { CommonButton, CommonOutlinedButton, CustomCalendarTodayOutlinedIcon, CustomDatePicker, CustomRequiredInputLabel, CustomTextFeild, StyledDateContainer } from "../../common/StyledComponents/StyledFields";
import commonStyles from "../../../App.module.scss";
import { apiRequest, catchErrorMessageHandler } from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import { ClipLoader } from "react-spinners";
import ErrorPopup from "../../common/ErrorPopup";
import constants from "../../../config/constants.json";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

interface ErrorMessageTypes {
  numOfShares: string;
  saleDate: string;
  companyName: string;
}

const Stocks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openPaymentCancelDialog, setOpenPaymentCancelDialog] = useState(false);
  const [formData, setFormData] = useState({
    numOfShares: "",
    saleDate: "",
    companyName: "",
  });
  const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
    numOfShares: "",
    saleDate: "",
    companyName: "",
  });
  const [receiptID, setReceiptID] = useState<string>("");
  const [userID, setUserID] = useState<string>("");
  const [itemsList, setItemList] = useState<any[]>([]);


  const validationSchema = object().shape({
    numOfShares: string().required("No of Shares is required"),
    saleDate: string().required("Sale Date is required"),
    companyName: string().required("Company Name is required"),
  });

  useEffect(() => {
    const storedCartItems = sessionStorage.getItem("cartItems");
    if (storedCartItems) {
      const cartItemObjects = JSON.parse(storedCartItems);
      setItemList(cartItemObjects);
    }
  }, []);

  const handleFieldChange = (field: string, value: any) => {
    if (field === "saleDate") {
      const formattedDate = dayjs(value, "MM-DD-YYYY");
      setDatePickerOpen(false);
      setFormData({
        ...formData,
        [field]: formattedDate.isValid()
          ? formattedDate.format("MM-DD-YYYY")
          : "",
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const handleDatePickerClick = () => {
    setDatePickerOpen(true);
  };

  const handleCancel = () => {
    setFormData({
      numOfShares: "",
      saleDate: "",
      companyName: "",
    });
  }

  useEffect(() => {
    setDatePickerOpen(false);
  }, [formData.saleDate]);

  const handleSave = async (): Promise<void> => {
    const errors: ErrorMessageTypes = {
      numOfShares: "",
      saleDate: "",
      companyName: "",
    };

    try {
      // Validate the form data against the validation schema
      await validationSchema.validate(formData, { abortEarly: false });
      // If validation passes, you can submit the form
      setErrorMessage(errors);
      // Call your API or perform other actions here
      saveApiCall();
    } catch (error: any) {
      // Handle validation errors

      if (error instanceof ValidationError) {
        error.inner.forEach((e: any) => {
          if (e.path) {
            errors[e.path as keyof ErrorMessageTypes] = e.message;
          }
        });
      }
      setErrorMessage(errors);
    }
  };



  const saveApiCall = () => {
    setLoading(true);
    const reqBody = {
      items: itemsList,
      modeOfPayment: 27,
      numberOfStocks: parseInt(formData.numOfShares),
      dateOfStocksTransferred: formData.saleDate,
      companyName: formData.companyName,
    };
    apiRequest({
      method: "POST",
      endpoint: apiPath.cartPayment,
      body: JSON.stringify(reqBody),
      includeToken: true
    })
      .then((res) => {
        setReceiptID(res.data.receiptId);
        setUserID(res.data.userId);
        sessionStorage.removeItem("cartItems");
        setFormData({
          numOfShares: "",
          saleDate: "",
          companyName: "",
        });
        setLoading(false);
        sessionStorage.setItem("showPaymentStatus", JSON.stringify(true));
        navigate(`/receipt?receiptID=${res.data.receiptId}&userID=${res.data.userId}`);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
        setLoading(false);
      });
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent the default form submission behavior
      handleSave();
    }
  };

  const handleButtons = () => {
    return (
      <div style={{ display: "flex", gap: "10px" }}>
        <CommonOutlinedButton
          className={commonStyles.cancelButton}
          onClick={handleCancel}

        >
          Cancel
        </CommonOutlinedButton>
        <CommonButton className={commonStyles.saveButton} onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <ClipLoader color="#fff" loading={loading} size={15} />
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "15px",
                  textAlign: "center",
                }}
              >
                Loading...
              </span>
            </>
          ) : (
            "Submit"
          )}
        </CommonButton>
      </div>
    );
  };

  const DatePicker = (): React.ReactElement => {
    return (
      <div style={{ borderRadius: "8px" }}>
        <StyledDateContainer
          onClick={handleDatePickerClick}
          sx={{
            backgroundColor: "#FFF9E4",
            borderRadius: "8px",
            height: "48px",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateTimePicker"]}>
              <CustomDatePicker
                open={isDatePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                slots={{
                  openPickerIcon: CustomCalendarTodayOutlinedIcon,
                }}
                slotProps={{
                  inputAdornment: {
                    position: "end",
                  },
                }}
                format="MM-DD-YYYY"
                value={
                  formData.saleDate
                    ? dayjs(formData.saleDate, "MM-DD-YYYY")
                    : ""
                }
                onChange={(value) => {
                  setErrorMessage({ ...errorMessage, saleDate: "" });
                  handleFieldChange("saleDate", value);
                }}
                className={
                  errorMessage.saleDate
                    ? styles.errorTextField
                    : styles.textField
                }
              />
            </DemoContainer>
          </LocalizationProvider>
          {errorMessage.saleDate ? (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {errorMessage.saleDate}
            </Typography>
          ) : (
            ""
          )}
        </StyledDateContainer>
      </div>
    );
  };

  const paymentComponent = () => {
    return (
      <form className={styles.ChequeContainer} style={{ width: "100%", marginTop: "20px" }} onKeyDown={handleKeyPress}>
        <Box className={styles.backArrow}>
          <Typography className={styles.heading} sx={{ fontSize: "20px" }}>Stocks Details</Typography>
        </Box>
        <Card className={styles.formContainer}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={4} item sx={{ marginRight: "40px" }}>
              {CustomRequiredInputLabel("No of Shares", true)}
              <CustomTextFeild
                fullWidth
                required
                value={formData.numOfShares}
                onChange={(e) => {
                  setErrorMessage({ ...errorMessage, numOfShares: "" });
                  let input = e.target.value.replace(/[^0-9]/g, ""); // Remove non-digit characters
                  // Disallow single '0'
                  if (input === "0") {
                    input = "";
                  }
                  const limitedInput = input.slice(0, 10); // Limit to 10 digits
                  handleFieldChange("numOfShares", limitedInput);
                }}
                className={
                  errorMessage.numOfShares
                    ? styles.errorTextField
                    : styles.textField
                }
                placeholder="Enter Number of Shares"
              />
              {errorMessage.numOfShares && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {errorMessage.numOfShares}
                </Typography>
              )}
            </Grid>
            <Grid xs={12} sm={4} item sx={{ marginRight: "40px" }}>
              {CustomRequiredInputLabel("Date of Sale", true)}
              {DatePicker()}
            </Grid>
            <Grid xs={12} sm={4} item sx={{ marginRight: "40px" }}>
              {CustomRequiredInputLabel("Company Name", true)}
              <CustomTextFeild
                fullWidth
                required
                value={formData.companyName}
                onChange={(e) => {
                  setErrorMessage({ ...errorMessage, companyName: "" });
                  handleFieldChange("companyName", e.target.value);
                }}
                className={
                  errorMessage.companyName
                    ? styles.errorTextField
                    : styles.textField
                }
                placeholder="Enter Company Name"
              />
              {errorMessage.companyName && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {errorMessage.companyName}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Card>
        <div>{handleButtons()}</div>
      </form>
    );
  };

  return (
    <Box>
      <Grid>
        {paymentComponent()}
      </Grid>
    </Box>
  )
}

export default Stocks